import type { MembershipPackage } from '@/api/config'
import type { PurchaseMode, VipPackage } from '@/api/pay'
import type { StoreProduct } from '@/utils/platform'

import { validateStoreProductContract } from './paymentContract'

export type DisplayPackage = MembershipPackage & {
  apple_product_id?: string
  is_auto_renew: boolean
  purchase_mode: PurchaseMode
  store_product?: StoreProduct
  store_price_required: boolean
  purchase_available: boolean
  purchase_error?: string
}

type BuildDisplayPackagesInput = {
  packages: VipPackage[]
  storeProducts?: StoreProduct[]
  storePriceRequired: boolean
  useUsdBackendPrice?: boolean
}

export function buildDisplayPackages({
  packages,
  storeProducts = [],
  storePriceRequired,
  useUsdBackendPrice = false,
}: BuildDisplayPackagesInput): DisplayPackage[] {
  const storeProductsById = new Map(
    storeProducts.map((product) => [product.product_id, product]),
  )

  return packages.map((pkg) => {
    const storeProduct = pkg.apple_product_id
      ? storeProductsById.get(pkg.apple_product_id)
      : undefined
    const contract = storePriceRequired
      ? validateStoreProductContract({
        packageId: pkg.package_id,
        appleProductId: pkg.apple_product_id,
        purchaseMode: pkg.purchase_mode,
        isAutoRenew: pkg.is_auto_renew,
        storeProduct,
      })
      : { valid: true as const }
    const backendPrice = useUsdBackendPrice && pkg.price_usd != null
      ? pkg.price_usd
      : pkg.price_cny
    const backendOriginalPrice = useUsdBackendPrice
      && (pkg.original_price_usd != null || pkg.price_usd != null)
      ? (pkg.original_price_usd ?? pkg.price_usd ?? pkg.original_price_cny ?? pkg.price_cny)
      : (pkg.original_price_cny ?? pkg.price_cny)

    return {
      id: pkg.package_id,
      name: pkg.name,
      name_zh: pkg.name_zh,
      name_en: pkg.name_en,
      price: storeProduct?.price ?? backendPrice,
      original_price: storeProduct?.price ?? backendOriginalPrice,
      currency_symbol: useUsdBackendPrice && pkg.price_usd != null ? '$' : '¥',
      points_gift: pkg.points_gift ?? 0,
      valid_days: pkg.duration_days,
      accent_color: pkg.accent_color || '#FF6B9D',
      gradient: pkg.gradient || 'linear-gradient(135deg, #FF6B9D, #A855F7)',
      benefits: pkg.benefits ?? [],
      tag: pkg.tag,
      tag_zh: pkg.tag_zh,
      tag_en: pkg.tag_en,
      is_recommended: pkg.is_recommended,
      apple_product_id: pkg.apple_product_id,
      is_auto_renew: pkg.is_auto_renew,
      purchase_mode: pkg.purchase_mode,
      store_product: storeProduct,
      store_price_required: storePriceRequired,
      purchase_available: contract.valid,
      purchase_error: contract.valid === false ? contract.reason : undefined,
    }
  })
}
